import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Review } from '../../model/paper';
import { ReviewsService } from '../../service/reviews.service';
import { ReviewStatus } from '../../enum/review.status';
import { SubmissionStatus } from '../../enum/submission.status';
import { NavbarService } from '../../service/navbar.service';
import { NO_PAGINATION } from 'src/app/model/pagination.options';
import { ShowAllPagedViewDirective } from 'src/app/class/paged-view-all';
import { LocalCache } from 'src/app/enum/local.cache';
import { PreferencesService } from 'src/app/service/preferences.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { Event } from 'src/app/model/event';
import { DefaultFilterPipe } from 'src/app/pipe/default-filter.pipe';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { InfoService } from 'src/app/service/info.service';
import { SelectionClipboardService } from 'src/app/service/selection-clipboard.service';
import { NotificationService } from 'src/app/service/notification.service';

class ReviewsByEvent {
  event: Event;
  count: number;
  reviews: Array<Review>;
  globalBlock: EventReviewConfiguration;
}

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss']
})

export class ReviewsComponent extends ShowAllPagedViewDirective implements AfterViewInit {
  public filterStatus: boolean = this.checkedFilterNotification;
  public reviewsByEvent: any[] = null;

  // `search` como setter pra re-equalizar colunas após o ngFor
  // adicionar/remover cards. Sem isso, digitar+backspace deixa cards que
  // sobreviveram ao filtro com `minWidth` calculado sobre o subset
  // filtrado, enquanto cards recém-remontados voltam sem `minWidth` →
  // colunas desincronizam entre os cards do mesmo evento.
  //
  // O reset+equalize é DEBOUNCED (300ms após a última tecla) — chamar a
  // cada keystroke fazia a página piscar (reset zerava minWidths, ngFor
  // reflowava, equalize re-fixava). Debouncing espera o usuário parar de
  // digitar antes de mexer no layout.
  private _search: string;
  private _searchEqualizeTimer: any = null;
  public get search(): string { return this._search; }
  public set search(value: string) {
    this._search = value;
    if (this._searchEqualizeTimer) { clearTimeout(this._searchEqualizeTimer); }
    this._searchEqualizeTimer = setTimeout(() => {
      const host: HTMLElement = this.elementRef?.nativeElement;
      if (host) {
        // Reset dos `minWidth` já aplicados — sem isso o max ficaria
        // travado no valor anterior (min-width restringe a redução
        // natural do conteúdo).
        host.querySelectorAll('.cv-eb-id-col, .cv-eb-actions-col, .cv-eb-files-aside')
          .forEach((el: any) => { el.style.minWidth = ''; });
      }
      this.equalizeCardColumns();
      this._searchEqualizeTimer = null;
    }, 300);
  }

  // Sticky header — flag de scroll usada pra ativar `.is-scrolled`
  // que mostra o box-shadow/border-bottom do header (Material-ish).
  public isScrolled = false;

  // Cards começam invisíveis até a equalização das colunas rodar — evita
  // o "dança" do primeiro paint pré-alinhamento.
  public cardsReady = false;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 4;
  }

  // Chave de localStorage onde os status selecionados são persistidos.
  private static readonly FILTER_STORAGE_KEY = 'jems3.reviews.filter.status';

  // Ordem fixa dos chips de status no menu de filtro (flex order via JS).
  private static readonly STATUS_ORDER: Record<string, number> = {
    notified: 0,   // Convidado
    confirmed: 1,  // Confirmado
    declined: 2,   // Declinado
    reminded: 3,   // Lembrado
    late: 4,       // Atrasado
    draft: 5,      // Rascunho
    completed: 6,  // Completado
  };

  // Seleção pra copy-to-clipboard. Set<id de review>.
  public selectedIds = new Set<number>();

  constructor(
    private reviewsService: ReviewsService,
    public navbarService: NavbarService,
    public preferencesService: PreferencesService,
    public defaultFilter: DefaultFilterPipe,
    public countryPipe: CountryPipe,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    public infoService: InfoService,
    private selectionClipboard: SelectionClipboardService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
  ) {
    super(preferencesService, LocalCache.OLD_REVIEWS);
  }

  // Preset ativo (query param `?preset=...`): view efêmera que NÃO
  // sobrescreve os filtros salvos do usuário. Vira false na primeira
  // interação do usuário com filtro/toggle. Presets suportados:
  //  - 'invites-pending' — só status NOTIFIED, sem eventos antigos.
  public _presetActive = false;
  // Status list corrente do preset em curso (pra plugar no
  // `submissions-list-filter[presetStatus]`). Vazio quando não há preset.
  public _presetStatus: string[] = [];
  // A 1ª emissão de filterNotification vem da inicialização do
  // submissions-list-filter (child ngOnInit chama getFilterNotification
  // com o checkedStatusFields). Não é ação do usuário — não deve limpar
  // o preset. A partir da 2ª chamada, aí sim é interação real de chip.
  private _presetInitConsumed = false;

  // Mapa de presets suportados → estado a aplicar. Cada preset é uma
  // view efêmera (não sobrescreve preferências); adicionar novos = nova
  // entrada aqui + link no badge correspondente da sidebar.
  private static readonly PRESETS: Record<string, { status: string[], showAll: boolean }> = {
    'invites-pending':   { status: ['NOTIFIED'],  showAll: false },
    'confirmed-reviews': { status: ['CONFIRMED'], showAll: false },
  };

  // Override do ngOnInit do ShowAllPagedViewDirective — precisamos
  // interceptar antes do `preferencesService.getPreference` e do load(),
  // pra plugar o preset (se houver ?preset=... no queryParams).
  ngOnInit(): void {
    setTimeout(() => {
      const preset = this.route.snapshot.queryParamMap.get('preset');
      const spec = preset ? ReviewsComponent.PRESETS[preset] : null;
      if (spec) {
        this._presetActive = true;
        this._presetStatus = spec.status.slice();
        this.filter.status = spec.status.slice() as ReviewStatus[];
        this.toggleNgModel = spec.showAll;
      } else {
        this.toggleNgModel = <boolean>this.preferencesService.getPreference(this.showAllPreferenceKey);
      }
      // Toggle do "incluir revisões antigas": qualquer clique do usuário
      // encerra o preset e volta a salvar a preferência normalmente.
      this.toggle?.change.subscribe((change: MatCheckboxChange) => {
        this._presetActive = false;
        this.preferencesService.changePreference(this.showAllPreferenceKey, change.checked);
      });
      this.load();
    });
  }

  public toggleSelection(id: number, checked: boolean): void {
    if (checked) { this.selectedIds.add(id); } else { this.selectedIds.delete(id); }
  }
  public clearSelection(): void { this.selectedIds.clear(); }
  public async copySelection(): Promise<void> {
    const flat: Review[] = (this.reviewsByEvent || []).flatMap((g: any) => g?.reviews || []);
    const items = flat.filter(r => this.selectedIds.has(r.id));
    // Notificação removida pra evitar o "pulo" da página (MatSnackBar +
    // cdk-overlay-container oscila a scrollbar do window momentaneamente).
    await this.selectionClipboard.copyReviews(items);
  }

  // True quando o usuário logado É o revisor (a revisão foi atribuída A ELE).
  // False quando ele delegou pra outra pessoa (assignedBy = ele).
  // Usado pelos cards pra mostrar SÓ a "outra parte" (uma das duas é sempre eu).
  // Filtra revisões pelo termo de busca E aplica a ordenação final:
  // (1) revisões próprias antes das delegadas, (2) submission.id asc.
  // Ordena aqui (não em getReviewsByEvent) porque é a última transformação
  // antes do *ngFor — garante que nenhum re-sort externo derrubaria o resultado.
  // trackBy do *ngFor — estabiliza a identidade dos cards mesmo quando
  // `filteredReviews` retorna uma array nova (slice+sort) a cada CD. Sem
  // isso, o Angular destruía/recriava os nós, apagando o `min-width`
  // setado por `equalizeCardColumns`.
  public trackReviewById(_index: number, review: any): any {
    return review?.id ?? review?.submission?.id;
  }

  // Cache do último resultado por (arrayRef, search) — sem isso o *ngFor
  // reexecuta `filteredReviews` a cada CD tick, escaneando N revisões e
  // gerando array novo. Cache preserva referência → trackBy funciona +
  // evita o hot path de normalizar strings a cada tick.
  private _filteredCache = new WeakMap<any[], { search: string; result: any[] }>();

  public filteredReviews(reviews: any[], search: string): any[] {
    if (!reviews) { return []; }
    if (!search) { return reviews; }
    const cached = this._filteredCache.get(reviews);
    if (cached && cached.search === search) { return cached.result; }
    const normalize = (str: string) =>
      String(str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const needle = normalize(search);
    // Filter preserva ordem do array de origem → resultado já sai ordenado.
    const result = reviews.filter(r => {
      // Autores (nome + e-mail + afiliação) — já exibidos no card de revisão, então buscar
      // por eles não expõe nada novo. Um autor sem perfil/afiliação não quebra (fallbacks).
      const authors = (r?.submission?.authors || []).map((a: any) => {
        const u = a?.user || {};
        const aff = u.profile?.affiliation;
        return `${u.name || ''} ${u.email || ''} ${aff?.acronym || ''} ${aff?.name || ''}`;
      }).join(' ');
      const fields = [
        r?.submission?.title,
        r?.submission?.event?.acronym,
        r?.submission?.event?.year,
        r?.submission?.event?.name,
        authors,
      ];
      return fields.some(v => v != null && normalize(String(v)).includes(needle));
    });
    this._filteredCache.set(reviews, { search, result });
    return result;
  }

  public isMeTheReviewer(review: any): boolean {
    return review?.user?.id === this.infoService.user?.id;
  }

  // Ícone do status pra usar no pill do card.
  public getStatusIcon(status: string) {
    return ReviewStatus.icon(status as ReviewStatus);
  }

  // URL completa do evento — `event.uri` armazena só o slug (ex.: "sbrc2025").
  // Endereço por extenso = `${window.location.origin}/${uri}`.
  public formatEventUrl(event: any): string {
    if (!event?.uri) { return ''; }
    return `${window.location.origin}/${event.uri}`;
  }

  // Formata local do evento: "City - State, Country".
  public formatEventLocation(event: any): string {
    const info = event?.eventInfo;
    if (!info) { return ''; }
    const cityState = [info.city, info.state].filter(v => !!v).join(' - ');
    const country = info.country ? this.countryPipe.transform(info.country) : '';
    return [cityState, country].filter(v => !!v).join(', ');
  }

  // Ícone do status da submissão pra usar no pill da linha superior.
  public getSubmissionStatusIcon(status: string) {
    return SubmissionStatus.icon(status as SubmissionStatus);
  }

  // Mapeia o arquivo a um ícone Material Symbols (vocabulário moderno usado
  // no novo card). FileType.icon() ainda retorna FontAwesome legacy (jems3s),
  // que continua válido nos cards clássicos — aqui sobrescrevemos só pro novo.
  public getFileMSIcon(file: any): string {
    const name = (file?.name || file?.file || '').toString().toLowerCase();
    const ext = name.split('.').pop() || '';
    switch (ext) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
      case 'odt':
      case 'rtf':
        return 'description';
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return 'folder_zip';
      case 'mp4':
      case 'mpg':
      case 'mov':
      case 'avi':
      case 'mkv':
        return 'movie';
      case 'ps':
        return 'article';
      case 'html':
      case 'htm':
        return 'code';
      case 'txt':
        return 'article';
      default:
        return 'attach_file';
    }
  }

  // Click-outside-to-close: fecha o painel de filtros se o click for em
  // qualquer lugar fora dele E fora do botão "Filtros" (que toggleia).
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filterVisible) {
      return;
    }
    const target = event.target as HTMLElement;
    const host = this.elementRef.nativeElement as HTMLElement;
    const inPanel = host.querySelector('.separator')?.contains(target);
    const inToggle = host.querySelector('.chair-header-icon-button--filter')?.contains(target);
    if (inPanel || inToggle) {
      return;
    }
    this.filterVisible = false;
  }

  // Teclado no painel aberto: Esc fecha, ↑/↓ navegam entre chips.
  // Space/Enter no checkbox foi tratado nativamente (input[type=checkbox]
  // toggleia ao Space e ao Enter via browser default).
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.filterVisible) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.filterVisible = false;
      // Devolve foco ao botão "Filtros" para continuidade da navegação.
      const filterBtn = this.elementRef.nativeElement.querySelector('.chair-header-icon-button--filter') as HTMLElement;
      filterBtn?.focus();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const panel = this.elementRef.nativeElement.querySelector('.separator') as HTMLElement;
      if (!panel) {
        return;
      }
      const checkboxes = Array.from(
        panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
      );
      if (checkboxes.length === 0) {
        return;
      }
      const currentIndex = checkboxes.findIndex(cb => cb === document.activeElement);
      let nextIndex: number;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % checkboxes.length;
      } else {
        nextIndex = currentIndex === -1
          ? checkboxes.length - 1
          : (currentIndex - 1 + checkboxes.length) % checkboxes.length;
      }
      event.preventDefault();
      checkboxes[nextIndex].focus();
    }
  }

  // Toggle do painel "Filtros" — substitui o `this.filterVisible = !this.filterVisible`
  // inline no template. Ao abrir, foca no primeiro checkbox automaticamente
  // (entry point natural pra navegação por teclado).
  toggleFilters(): void {
    this.filterVisible = !this.filterVisible;
    if (this.filterVisible) {
      // Aguarda renderização + animação inicial para focar o primeiro chip.
      setTimeout(() => {
        const firstCheckbox = this.elementRef.nativeElement.querySelector(
          '.separator input[type="checkbox"]'
        ) as HTMLInputElement;
        firstCheckbox?.focus();
      }, 50);
    }
  }

  // Smart toggle: se todos marcados, desmarca todos. Caso contrário, marca todos.
  // stopPropagation pelo mesmo motivo de clearFilters (o botão pode trocar de
  // label e o listener interpretaria como "fora do painel").
  toggleAllFilters(event?: MouseEvent): void {
    event?.stopPropagation();
    const allChecked = this.allStatusesSelected;
    const selector = allChecked
      ? '.submissions-list-filter .filter-status-option:not(.status-off)'
      : '.submissions-list-filter .filter-status-option.status-off';
    const buttons: NodeListOf<HTMLButtonElement> = this.elementRef.nativeElement.querySelectorAll(selector);
    buttons.forEach(b => b.click());
  }

  get allStatusesSelected(): boolean {
    return this.filter?.status?.length === this.reviewStatusFields.length;
  }

  load() {
    setTimeout(() => {
      this.myReviews = true;
      this.getReviews();
    });
  }

  // Override do updateToggle do parent — mesma ação, mas encerra o preset
  // (é ação explícita do usuário sobre "incluir revisões antigas").
  updateToggle(): void {
    this._presetActive = false;
    super.updateToggle();
  }

  get reviews(): Array<Review> {
    return <Array<Review>> this.elements;
  }

  public getReviews(): void {
    this.navbarService.loadingStart();
    this.reviewsByEvent = null;
    const paginationOptions = NO_PAGINATION;
    paginationOptions.show = this.showAll;
    this.reviewsService.getUserReviews(paginationOptions, this.filter, undefined, true).subscribe(pagination => {
      this.unfilteredElements = pagination.items as Review[];
      this.filterPapers(<Review[]>this.unfilteredElements);
      this.getReviewsByEvent();
      this.navbarService.loadingStop();
      window.scroll(0, 0);
      setTimeout(() => this.renderStatusCounts(), 150);
      // Um único equalize pós-load — se paper-review-actions terminar de
      // montar mais tarde, o ResizeObserver instalado em ngAfterViewInit
      // pega a mudança e re-iguala. Evita os 3 layouts síncronos legados.
      setTimeout(() => this.equalizeCardColumns(), 200);
    });
  }

  // Iguala largura das colunas 1 (ID) e 3 (arquivos) em todos os cards da
  // página. Cada coluna assume a largura intrínseca do conteúdo MAIOR — não
  // é possível com CSS puro (cards são elementos independentes), então
  // medimos via DOM e aplicamos `min-width` em todos os elementos da mesma
  // coluna. Reexecuta também no resize da janela.
  @HostListener('window:resize')
  onWindowResizeForCols(): void {
    this.equalizeCardColumns();
  }

  private equalizeCardColumns(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    const ids = Array.from(host.querySelectorAll('.cv-eb-id-col')) as HTMLElement[];
    const actions = Array.from(host.querySelectorAll('.cv-eb-actions-col')) as HTMLElement[];
    const asides = Array.from(host.querySelectorAll('.cv-eb-files-aside')) as HTMLElement[];
    if (!ids.length && !actions.length && !asides.length) { return; }

    // Idempotente: se todos os cols de um sel já têm o mesmo width, pula
    // — sem reset+set (evita loop com ResizeObserver).
    const applyMax = (els: HTMLElement[]) => {
      if (els.length < 2) { return; }
      const widths = els.map(e => e.offsetWidth);
      const max = Math.max(...widths);
      if (widths.every(w => w === max)) { return; }
      const px = `${max}px`;
      els.forEach(el => { el.style.minWidth = px; });
    };
    applyMax(ids);
    applyMax(actions);
    applyMax(asides);
    this.cardsReady = true;
  }

  // Conta revisões por status (cross-event, ignora search/filtro atual).
  // Usado pra injetar contador ao lado de cada chip no menu de status.
  private getStatusCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.reviewStatusFields.forEach(s => counts[String(s).toLowerCase()] = 0);
    const all = (this.unfilteredElements as Review[]) || [];
    all.forEach(r => {
      const key = String(r.status || '').toLowerCase();
      if (counts[key] !== undefined) {
        counts[key]++;
      }
    });
    return counts;
  }

  // Injeta/atualiza um <span class="status-count">N</span> dentro de cada
  // label do menu de status, após o chip <span> colorido. Idempotente: se
  // já existe, só atualiza o texto.
  // Contagem por status — passada ao filter via [statusCounts].
  public statusCounts: Record<string, number> = {};

  private renderStatusCounts(): void {
    this.statusCounts = this.getStatusCounts();
    const rows: NodeListOf<HTMLElement> = this.elementRef.nativeElement.querySelectorAll(
      '.submissions-list-filter .filter-status-option'
    );
    rows.forEach(row => {
      const statusClass = (Array.from(row.classList).find(c => c.startsWith('status-') && c !== 'status-off') || '').replace('status-', '');
      if (!statusClass) return;
      row.style.order = String(ReviewsComponent.STATUS_ORDER[statusClass] ?? 99);
    });
  }

  public getReviewsByEvent() {
    const reviews: Array<Review> = this.reviews;
    if (!reviews || reviews.length === 0) {
      this.reviewsByEvent = [];
      return;
    }

    // Dedup + agrupamento em UMA passada O(N) via Map.
    const eventsById = new Map<number, Event>();
    const reviewsByEventId = new Map<number, Review[]>();
    for (const r of reviews) {
      const ev = r.submission?.event;
      if (!ev) { continue; }
      if (!eventsById.has(ev.id)) {
        eventsById.set(ev.id, ev);
        reviewsByEventId.set(ev.id, []);
      }
      reviewsByEventId.get(ev.id).push(r);
    }

    // ZERO fan-outs: `blockReview` e `uri`/`eventInfo` já vêm inline no
    // payload do `byUser` (ver review.py `format_submission_my_reviews`).
    // Antes: 2·N HTTPs sequenciais (globalBlock + getEvent per-event).
    // Sem plainToClass — o construtor de ReviewsByEvent é plain object;
    // não há @Type/decorators, então class-transformer não agrega valor
    // e ainda re-serializa `reviews` perdendo instâncias.
    const groups: ReviewsByEvent[] = Array.from(eventsById.values()).map(e => {
      const sortedReviews = reviewsByEventId.get(e.id).slice().sort((a, b) => {
        const aMine = this.isMeTheReviewer(a) ? 0 : 1;
        const bMine = this.isMeTheReviewer(b) ? 0 : 1;
        if (aMine !== bMine) { return aMine - bMine; }
        return (a.submission?.id ?? 0) - (b.submission?.id ?? 0);
      });
      const g = new ReviewsByEvent();
      g.event = e;
      g.reviews = sortedReviews;
      g.count = sortedReviews.length;
      // blockReview inline — casa com o shape antigo `{block_review}`.
      const blockReview = (e as any)?.blockReview ?? (e as any)?.block_review ?? false;
      g.globalBlock = { blockReview } as any;
      return g;
    }).sort((a, b) => a.event.id - b.event.id);

    this.reviewsByEvent = groups;
    setTimeout(() => this.equalizeCardColumns(), 50);
  }

  updateReviewsByFilter(): void {
    this.reviewsByEvent?.map(eventList => {
      eventList.reviews?.map(r => {
        r.visible = this.filter.status.includes(r.status);
      });
    });
    // Filtro pode ocultar/mostrar cards — re-iguala colunas.
    setTimeout(() => this.equalizeCardColumns(), 0);
  }

  ifVisibleReviews(reviews: Array<Review>): boolean {
    return reviews.some((r) => r.visible === true);
  }

  stopClickPropagation(event): void {
    event.stopPropagation();
  }

  get checkedFilterNotification(): boolean {
    return this.reviewStatusFields.length > this.cachedStatusFields.length ? true : false;
  }

  get cachedStatusFields(): Array<string> {
    const cachedStatus = this.preferencesService.getPreference(LocalCache.STATUS_REVIEWS);
    if (cachedStatus === undefined || cachedStatus === false) {
      // No cached value defined. Set all fields as checked.
      return this.reviewStatusFields;
    } else if (cachedStatus === '') {
      // Cached value is defined with no status marked.
      return [];
    }
    // Return array of string status that are marked.
    return (<string>cachedStatus).split(',');
  }

  get reviewStatusFields(): Array<string> {
    return ReviewStatus.toIterable();
  }

  getFilterStatus(status: boolean) {
    this.filterStatus = status;
    // Preset: 1ª chamada = init do child, ignora. 2ª+ = ação do usuário
    // (chip toggle), encerra o preset e volta a salvar normalmente.
    if (this._presetActive) {
      if (this._presetInitConsumed) {
        this._presetActive = false;
      } else {
        this._presetInitConsumed = true;
      }
    }
    this.updateReviewsByFilter();
    // Adia pra microtask: os chip-checkboxes emitem este evento DURANTE o
    // ciclo de CD do init; atualizar filterActiveCount sincronamente dispara
    // ExpressionChangedAfterItHasBeenChecked. Promise.then roda após o CD.
    Promise.resolve().then(() => {
      this.recomputeFilterActiveCount();
      this.saveFiltersToStorage();
    });
  }

  private _cardsResizeObserver: ResizeObserver | null = null;
  private _equalizeRafHandle: number | null = null;

  ngAfterViewInit(): void {
    // Restaura status persistidos do localStorage. Espera os chip-checkboxes
    // inicializarem (eles usam setTimeout no próprio ngOnInit). Preset ativo
    // pula o restore — a intenção é uma view efêmera, não o estado salvo.
    setTimeout(() => {
      if (!this._presetActive) {
        this.restoreFiltersFromStorage();
      }
      this.renderStatusCounts();
      this.recomputeFilterActiveCount();
      // Estabiliza os bindings após as mudanças assíncronas (restore + chips).
      this.cdr.detectChanges();
    }, 150);

    // ResizeObserver captura mudanças tardias em `.main-body` (paper-review-
    // actions monta seus botões async → colunas mudam de tamanho depois do
    // primeiro equalize). Substitui o triple-schedule 200/600/1200 do load.
    if (typeof ResizeObserver === 'undefined') { return; }
    const host: HTMLElement = this.elementRef?.nativeElement;
    const body = host?.querySelector('.main-body') as HTMLElement | null;
    if (!body) { return; }
    this._cardsResizeObserver = new ResizeObserver(() => {
      if (this._equalizeRafHandle) { return; }
      this._equalizeRafHandle = requestAnimationFrame(() => {
        this._equalizeRafHandle = null;
        this.equalizeCardColumns();
      });
    });
    this._cardsResizeObserver.observe(body);
  }

  ngOnDestroy(): void {
    if (this._cardsResizeObserver) { this._cardsResizeObserver.disconnect(); }
    if (this._equalizeRafHandle) { cancelAnimationFrame(this._equalizeRafHandle); }
  }

  private saveFiltersToStorage(): void {
    // Preset ativo → view efêmera, não escreve por cima do estado salvo.
    if (this._presetActive) { return; }
    try {
      if (this.filter?.status) {
        localStorage.setItem(
          ReviewsComponent.FILTER_STORAGE_KEY,
          JSON.stringify(this.filter.status)
        );
      }
    } catch {
      // localStorage pode estar indisponível (modo privado / quota) — ignora.
    }
  }

  private restoreFiltersFromStorage(): void {
    let savedStatuses: string[] | null = null;
    try {
      const raw = localStorage.getItem(ReviewsComponent.FILTER_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          savedStatuses = parsed;
        }
      }
    } catch {
      return;
    }
    if (!savedStatuses) {
      return;
    }
    // Para cada status que NÃO deveria estar selecionado mas está checked,
    // clica programaticamente pra desmarcar — propagando ao chip-checkbox
    // visual + ao filter.status array via toggle interno.
    const allStatuses = this.reviewStatusFields;
    const shouldUncheck = allStatuses.filter(s => !savedStatuses.includes(s));
    shouldUncheck.forEach(status => {
      const btn = this.elementRef.nativeElement.querySelector(
        `.submissions-list-filter .filter-status-option.status-${status.toLowerCase()}`
      ) as HTMLButtonElement | null;
      if (btn && !btn.classList.contains('status-off')) {
        btn.click();
      }
    });
  }

  public isFiltered(): boolean {
    return this.filter.status.length < this.reviewStatusFields.length;
  }

  // Nº de status excluídos do filtro (= filtros ativos). CACHEADO num campo
  // (não getter) — recalcular a cada change-detection causava
  // ExpressionChangedAfterItHasBeenChecked, pois os chip-checkboxes populam
  // filter.status DEPOIS do 1º render. Atualizado só em eventos (handlers).
  public filterActiveCount = 0;

  private recomputeFilterActiveCount(): void {
    if (!this.filter?.status) {
      this.filterActiveCount = 0;
      return;
    }
    const total = this.reviewStatusFields.length;
    const selected = this.filter.status.length;
    this.filterActiveCount = selected < total ? total - selected : 0;
  }

  public hasFilterResults(): boolean {
    // `.some` short-circuit (vs filter+length>0).
    return this.reviewsByEvent.some(rbe => this.hasFilterResultsEvent(rbe));
  }

  public hasFilterResultsEvent(reviewsByEvent): boolean {
    // Sem busca → todas as revisões "passam"; evita o transform pesado
    // (defaultFilter ainda sorta in-place). Caso comum no load default.
    if (!this.search) {
      return (reviewsByEvent.reviews?.length ?? 0) > 0;
    }
    // Reusa o cache de `filteredReviews` — mesmo needle → hit instantâneo.
    return this.filteredReviews(reviewsByEvent.reviews, this.search).length > 0;
  }
}
